import { isSearchTooLong } from "@/helpers/searchLimit";
import { ref, type Ref, onBeforeUnmount } from "vue";
import { useSearchQuery } from "@/composables/useDebouncedSearch";
import { useLatestRequest } from "@/composables/useLatestRequest";
import { isAbortError } from "@/helpers/abort";

export interface UseAsyncAutocompleteItemsOptions<TItem> {
	fetchItems: (searchTerm: string, signal?: AbortSignal) => Promise<TItem[]>;
	mapItems?: (items: TItem[]) => TItem[];
	/**
	 * Provide currently selected item(s) so the list can include them when not present in results.
	 * Return a single item, an array of items, string ID(s), or undefined when nothing is selected.
	 * Strings are treated as IDs and will be fetched via fetchByIds if not present in results.
	 */
	getSelected?: () => TItem | TItem[] | string | string[] | undefined;
	/**
	 * Optional: fetch items by their IDs when the selected values are not present in the results.
	 */
	fetchByIds?: (ids: string[], signal?: AbortSignal) => Promise<TItem[]>;
	/**
	 * Optional: get unique identifier for an item (defaults to item["id"]).
	 */
	getId?: (item: TItem) => string;
}

export function useAsyncAutocompleteItems<TItem>({
	fetchItems,
	mapItems,
	getSelected,
	fetchByIds,
	getId
}: UseAsyncAutocompleteItemsOptions<TItem>) {
	const items: Ref<TItem[]> = ref([]);
	const isLoading: Ref<boolean> = ref(false);
	const requestScope = useLatestRequest();

	const updateItems = async (term: string) => {
		if (isSearchTooLong(term)) return;
		const { requestId, signal } = requestScope.startRequest();
		isLoading.value = true;
		try {
			const results = await fetchItems(term, signal);
			if (!requestScope.isCurrent(requestId)) {
				return;
			}
			const selected = getSelected?.();
			const selectedArrayRaw: (TItem | string)[] = Array.isArray(selected)
				? (selected as (TItem | string)[])
				: selected
				? [selected as unknown as TItem | string]
				: [];

			const getItemId = (item: TItem): string => {
				if (getId) return getId(item);
				const anyItem = item as unknown as Record<string, any>;
				return anyItem?.id ?? "";
			};

			// Build set of ids present in fetched results
			const resultIds = new Set<string>(
				results
					.map((r) => {
						try {
							return getItemId(r);
						} catch {
							return "";
						}
					})
					.filter((x) => !!x)
			);

			// Determine which selected ids are missing from the results
			const selectedIds: string[] = selectedArrayRaw
				.map((s) => (typeof s === "string" ? s : getItemId(s as TItem)))
				.filter((x) => !!x);

			const selectedResolved: TItem[] = [];
			if (selectedIds.length > 0) {
				const missingIds = selectedIds.filter((id) => !resultIds.has(id));
				// If the caller provided full objects in selectedArrayRaw, include those first
				const providedObjects: TItem[] = selectedArrayRaw.filter((s) => typeof s !== "string").map((s) => s as TItem);

				selectedResolved.push(...providedObjects);

				// Fetch any missing selected items by id
				if (fetchByIds && missingIds.length > 0) {
					try {
						const fetchedSelected = await fetchByIds(missingIds, signal);
						if (!requestScope.isCurrent(requestId)) {
							return;
						}
						selectedResolved.push(...fetchedSelected);
					} catch (error) {
						if (isAbortError(error)) {
							return;
						}
						// Ignore fetchByIds failures; proceed with what we have
					}
				}
			}

			// Combine and de-duplicate by id, keeping selectedResolved items first
			const combined = [...selectedResolved.filter(Boolean), ...results];
			const seen = new Set<string>();
			const deduped: TItem[] = [];
			for (const item of combined) {
				const id = getItemId(item);
				const key = id || JSON.stringify(item);
				if (!seen.has(key)) {
					seen.add(key);
					deduped.push(item);
				}
			}

			items.value = mapItems ? mapItems(deduped) : deduped;
		} catch (error) {
			if (isAbortError(error)) {
				return;
			}
			throw error;
		} finally {
			if (!requestScope.isSuperseded(requestId)) {
				isLoading.value = false;
			}
		}
	};

	const { searchTerm, search, setValueSilent } = useSearchQuery({
		debounceMs: 500,
		searchCallback: updateItems
	});

	const refresh = () => updateItems(searchTerm.value);

	onBeforeUnmount(() => {
		requestScope.abortCurrent();
		isLoading.value = false;
	});

	return {
		items,
		isLoading,
		searchTerm,
		setValueSilent,
		onSearchInput: search,
		refresh
	};
}
